<template>
  <div class="agents-panel">
    <p class="panel-desc">
      Selecciona los agentes que este tenant puede usar y distribuir a sus subtenants.
      Solo el administrador de plataforma puede modificar esta lista.
    </p>

    <div v-if="loading" class="loading-msg"><i class="pi pi-spin pi-spinner" /> Cargando...</div>

    <div v-else class="agents-list">
      <div v-for="agent in allAgents" :key="agent.id" class="agent-row">
        <div class="agent-info">
          <div :class="['agent-avatar', agent.category]">
            <i :class="['pi', agentIcon(agent.category)]" />
          </div>
          <div>
            <div class="agent-name">{{ agent.name }}</div>
            <div class="agent-cat">{{ agent.category }}</div>
          </div>
        </div>
        <ToggleButton
          :modelValue="isEnabled(agent.id)"
          onLabel="Habilitado"
          offLabel="Deshabilitado"
          onIcon="pi pi-check"
          offIcon="pi pi-times"
          :loading="toggling[agent.id]"
          @update:modelValue="toggle(agent.id, $event)"
        />
      </div>
    </div>

    <Toast />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import ToggleButton from 'primevue/togglebutton';
import Toast from 'primevue/toast';
import api from '../../services/api';

const props = defineProps({
  tenantId: { type: String, required: true },
  allAgents: { type: Array, default: () => [] },
});
const emit = defineEmits(['updated']);
const toast = useToast();
const enabledAgentIds = ref(new Set());
const loading = ref(false);
const toggling = ref({});

const agentIcon = (cat) => ({ financiero: 'pi-chart-line', soporte: 'pi-headphones', ventas: 'pi-shopping-cart', general: 'pi-android' }[cat] || 'pi-android');
const isEnabled = (agentId) => enabledAgentIds.value.has(agentId);

const load = async () => {
  loading.value = true;
  try {
    const res = await api.get(`/tenants/${props.tenantId}/agents`);
    enabledAgentIds.value = new Set(res.data.map(a => a.agent_id));
  } catch (e) { console.error(e); }
  finally { loading.value = false; }
};

const toggle = async (agentId, enable) => {
  toggling.value[agentId] = true;
  try {
    if (enable) {
      await api.post(`/tenants/${props.tenantId}/agents`, { agent_id: agentId });
      enabledAgentIds.value.add(agentId);
      toast.add({ severity: 'success', summary: 'Agente habilitado', life: 2000 });
    } else {
      await api.delete(`/tenants/${props.tenantId}/agents/${agentId}`);
      enabledAgentIds.value.delete(agentId);
      toast.add({ severity: 'info', summary: 'Agente deshabilitado', life: 2000 });
    }
    emit('updated');
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  } finally {
    toggling.value[agentId] = false;
  }
};

onMounted(load);
</script>

<style scoped>
.agents-panel { display: flex; flex-direction: column; gap: 0.75rem; }
.panel-desc { font-size: 0.82rem; color: #6b7280; margin: 0; }
.loading-msg { text-align: center; color: #9ca3af; padding: 1rem; }
.agents-list { display: flex; flex-direction: column; gap: 0.5rem; }
.agent-row { display: flex; align-items: center; justify-content: space-between; padding: 0.6rem 0.875rem; border: 1px solid #e5e7eb; border-radius: 8px; }
.agent-info { display: flex; align-items: center; gap: 0.75rem; }
.agent-avatar { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 1rem; }
.agent-avatar.financiero { background: #dbeafe; color: #1d4ed8; }
.agent-avatar.soporte { background: #dcfce7; color: #15803d; }
.agent-avatar.ventas { background: #fef3c7; color: #b45309; }
.agent-avatar.general { background: #f3e8ff; color: #7c3aed; }
.agent-name { font-weight: 600; font-size: 0.875rem; color: #1e293b; }
.agent-cat { font-size: 0.75rem; color: #6b7280; }
</style>
